import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TipoDivergenciaEnum } from '@/services/conciliacao-fiscal';

interface DivergenciasSummaryCardsProps {
  resumoPorTipo: Record<TipoDivergenciaEnum, { total: number }>;
}

export default function DivergenciasSummaryCards({ resumoPorTipo }: DivergenciasSummaryCardsProps) {
  const getTipoConfig = (tipo: TipoDivergenciaEnum) => {
    const configs = {
      [TipoDivergenciaEnum.PULO_NUMERACAO]: {
        icon: '🔄',
        label: 'Pulos VAR',
        description: 'Lacunas na sequência de cupons',
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      },
      [TipoDivergenciaEnum.EXCLUSIVO_DOC001]: {
        icon: '📄',
        label: 'Exclusivos VAR↔Migrate',
        description: 'Apenas no VAR',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      },
      [TipoDivergenciaEnum.EXCLUSIVO_VALIDATOR]: {
        icon: '✓',
        label: 'Exclusivos Migrate↔VAR',
        description: 'Apenas na Migrate',
        color: 'text-purple-600',
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200'
      },
      [TipoDivergenciaEnum.STATUS_DIVERGENTE]: {
        icon: '⚠️',
        label: 'Status VAR↔Migrate',
        description: 'Divergências VAR vs Migrate',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      },
      // GFT Livro Fiscal types
      [TipoDivergenciaEnum.PULO_NUMERACAO_GFT]: {
        icon: '📈',
        label: 'Pulos GFT',
        description: 'Lacunas na sequência GFT',
        color: 'text-cyan-600',
        bgColor: 'bg-cyan-50',
        borderColor: 'border-cyan-200'
      },
      [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT]: {
        icon: '🔄',
        label: 'Status VAR↔GFT',
        description: 'Divergências VAR vs GFT',
        color: 'text-cyan-600',
        bgColor: 'bg-cyan-50',
        borderColor: 'border-cyan-200'
      },
      [TipoDivergenciaEnum.EXCLUSIVO_GFT_LIVRO]: {
        icon: '📗',
        label: 'Exclusivos GFT↔VAR',
        description: 'Apenas no GFT Livro Fiscal',
        color: 'text-lime-600',
        bgColor: 'bg-lime-50',
        borderColor: 'border-lime-200'
      },
      [TipoDivergenciaEnum.EXCLUSIVO_DOC001_GFT]: {
        icon: '📘',
        label: 'Exclusivos VAR↔GFT',
        description: 'Apenas no VAR (vs GFT)',
        color: 'text-sky-600',
        bgColor: 'bg-sky-50',
        borderColor: 'border-sky-200'
      },
      [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT_VALIDATOR]: {
        icon: '🔀',
        label: 'Status GFT↔Migrate',
        description: 'Divergências GFT vs Migrate',
        color: 'text-indigo-600',
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200'
      }
    };
    return configs[tipo];
  };

  const totalGeral = Object.values(resumoPorTipo).reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">

      {/* Type-specific cards */}
      {Object.entries(resumoPorTipo).map(([tipo, data]) => {
        const config = getTipoConfig(tipo as TipoDivergenciaEnum);
        const porcentagem = totalGeral > 0 ? ((data.total / totalGeral) * 100).toFixed(1) : '0';

        return (
          <Card
            key={tipo}
            className={`${config.bgColor} ${config.borderColor} border transition-all hover:shadow-md`}
          >
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {config.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <div className={`text-2xl font-bold ${config.color}`}>
                    {data.total.toLocaleString()}
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {porcentagem}%
                  </Badge>
                </div>
                <div className="text-2xl" role="img" aria-label={config.label}>
                  {config.icon}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {config.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}